import { ITEMS_PER_PAGE_10, useSearchParams, capitalizeAll, PaginationBar, ITEMS_PER_PAGE_LIST, PAGING_MIN_PAGES, GenericMessageBox }
    from '@moderepo/mode-app-components';
import React, { useCallback, useContext, useMemo, useState } from 'react';
import { Agent, AgentRole, AlertConditionType } from '@moderepo/mode-internal-apis';
import { modeUIContext, showSlideOutPanel, SlideOutPanelLocation } from '@moderepo/mode-ui-state';
import { ritzDataContext, selectLoggedInAgent, selectProjectAgent } from '@moderepo/mode-data-state-ritz';
import { UIFetchAlertRulesFilters } from '@moderepo/mode-data-state-user-app';
import { AlertRule, isMetricsAlertCondition } from '@moderepo/mode-apis';
import { useSelectAlertRules, useSelectProject } from '../../utils/dataStateHooks';
import { RitzComponentsCollection } from '../../utils/ritzComponentsCollection';
import { AlertRulesTable, AlertRulesTableDataItem, AlertRulesTableProps } from '../../components';
import { useRitzTranslation } from '../../utils/customHooks';
import { AlertRulePreviewCompOptions, SlideOutPanelCompType } from '../routes/defaultProjectConfig';
import { getDateTimeAndElapsedTime } from '../containerUtils';
import { getAlertConditionTypeDisplayName, getEntityClassDisplayValue } from '.';
import { useSelectTranslatedEntityClasses } from '..';



/**
 * The interface for the list of filters current selected.
 * TODO - Add more filters base on what the backend support.
 */
interface AlertRuleSearchFields {
    readonly entityClass: string | undefined
}


/**
 * By default, we will show all columns of the AlertRulesTable. However, if the container wants to hide some columns, it can specify the
 * set of column it want to hide and these are the columns that can be customized.
 */
export interface AlertRulesTableHiddenFields {
    readonly id?: boolean | undefined;
    readonly name?: boolean | undefined;
    readonly description?: boolean | undefined;
    readonly enabled?: boolean | undefined;
    readonly conditionType?: boolean | undefined;
    readonly targetEntityClass?: boolean | undefined;
    readonly updatedAt?: boolean | undefined;
    readonly createdAt?: boolean | undefined;
}


/**
 * This is the set of attributes required by the AlertRulesListingContainer
 */
export interface AlertRulesListingContainerProps {
    readonly projectId: number;

    // Customized page size value. If not provided, we will use 10 as default
    readonly itemsPerPage?: number | undefined;

    // Customized set of Alert Rules table fields the parent container wants to hide
    readonly hiddenFields?: AlertRulesTableHiddenFields | undefined;
}


/**
 * This is a container used for displaying lists of Alert Rules in tabular format.
 */
export const AlertRulesListingContainer: React.FC<AlertRulesListingContainerProps> = (props: AlertRulesListingContainerProps) => {

    const { dispatch:uiDispatch } = useContext(modeUIContext);
    const { state:dataState } = useContext(ritzDataContext);
    const project = useSelectProject(props.projectId);
    // Get the logged in Agent data for the given project. This is is not the same as loggedInAgent data.
    const loggedInAgent = selectLoggedInAgent(dataState) as Agent;
    const projectAgent = loggedInAgent ? selectProjectAgent(dataState, project?.id, loggedInAgent.id) : undefined;
    const canEditAlertRules = projectAgent?.role === AgentRole.ADMIN;
    const { trans } = useRitzTranslation(project);
    const entityClasses = useSelectTranslatedEntityClasses(props.projectId, trans);



    // State for managing the pagination and filters
    const {
        searchParams, onItemsPerPageChange, onPrevPage, onNextPage, onPage,
    } = useSearchParams<AlertRuleSearchFields>({
        searchText  : '',
        itemsPerPage: props.itemsPerPage || ITEMS_PER_PAGE_10,
        currentPage : 0,
        otherFields : {
            entityClass: undefined,
        },
    });


    const filters = useMemo((): UIFetchAlertRulesFilters => {
        return {
            pageNumber : searchParams.currentPage,
            pageSize   : searchParams.itemsPerPage,
            entityClass: searchParams.otherFields.entityClass,
        };
    }, [searchParams.currentPage, searchParams.itemsPerPage, searchParams.otherFields.entityClass]);
    const alertRules = useSelectAlertRules(props.projectId, filters);


    // The state to keep track of the selected alert rule.
    const [selectedAlertRule, setSelectedAlertRule] = useState<AlertRule | undefined>();


    const onAlertRuleSelected = useCallback((alertRuleTarget: AlertRule | AlertConditionType) => {
        if (typeof alertRuleTarget === 'object') {
            setSelectedAlertRule(alertRuleTarget);
        }

        // Dispatch an action to show the Preview Panel for the selected alertRule.
        const options: AlertRulePreviewCompOptions = {
            location : SlideOutPanelLocation.RIGHT,
            compType : SlideOutPanelCompType.ALERT_RULE_PREVIEW,
            compProps: {
                data: {
                    projectId      : props.projectId,
                    targetAlertRule: typeof alertRuleTarget === 'object' ? alertRuleTarget.id : alertRuleTarget,
                },
                enableDeleteAlertRule: true,
            },
        };
        uiDispatch(showSlideOutPanel(options));
    }, [props.projectId, uiDispatch]);



    /**
     * Localize the current list of alert rules object. This data will be passed to the AlertRulesTable component for display.
     */
    const localizedAlertRulesList = useMemo((): readonly AlertRulesTableDataItem[] | undefined => {
        if (alertRules) {
            return alertRules.items.map((alertRule): AlertRulesTableDataItem => {

                const targetEntityClass = ((): string => {
                    // Get the entity class from the alert rule's condition. Only heartbeat and threshold alert condition type
                    // have  'metricsCondition' settings
                    if (isMetricsAlertCondition(alertRule.condition)) {
                        // Find the entityClass object for the metricsCondition.entityClass and use the entityClass object's display name if possible
                        // If not, use the entityClass name as is.
                        return getEntityClassDisplayValue(alertRule.condition.metricsCondition.entityClass, entityClasses);
                    }

                    return trans('label.unknown');
                })();


                return {
                    actualValue : alertRule,
                    selected    : selectedAlertRule?.id === alertRule.id,
                    displayValue: {
                        id           : alertRule.id.toString(),
                        name         : alertRule.name,
                        description  : alertRule.description,
                        targetEntityClass,
                        conditionType: capitalizeAll(trans(
                            getAlertConditionTypeDisplayName(alertRule.condition.conditionType) ?? 'label.unknown',
                        )),
                        createdAt: getDateTimeAndElapsedTime(alertRule.creationTime, trans, false),
                        updatedAt: getDateTimeAndElapsedTime(alertRule.updateTime, trans, false),
                        enabled  : trans(alertRule.enabled ? 'label.enabled' : 'label.disabled'),
                    },
                };
            });
        }
        return undefined;
    }, [alertRules, entityClasses, selectedAlertRule?.id, trans]);



    /**
     * Set up the Alert Rules table's fields and cache it. We will pass this settings to the AlertRulesTable.
     */
    const alertRulesTableFieldsSettings = useMemo((): AlertRulesTableProps['fieldsSettings'] => {
        return {
            fields: {
                id: {
                    label : capitalizeAll(trans('label.id')),
                    hidden: props.hiddenFields?.id,
                },
                name: {
                    label : capitalizeAll(trans('label.name')),
                    hidden: props.hiddenFields?.name,
                },
                conditionType: {
                    label : capitalizeAll(trans('alerting.alert_condition_type')),
                    hidden: props.hiddenFields?.conditionType,
                },
                targetEntityClass: {
                    label : capitalizeAll(trans('alerting.target_entity_class')),
                    hidden: props.hiddenFields?.targetEntityClass,
                },
                createdAt: {
                    label : capitalizeAll(trans('label.date_created')),
                    hidden: props.hiddenFields?.createdAt,
                },
                enabled: {
                    label : capitalizeAll(trans('label.status')),
                    hidden: props.hiddenFields?.enabled,
                },
            },
        };
    }, [props.hiddenFields?.conditionType, props.hiddenFields?.createdAt, props.hiddenFields?.enabled,
        props.hiddenFields?.id, props.hiddenFields?.name, props.hiddenFields?.targetEntityClass, trans]);


    // Create PaginationBar and cache it. We will pass this component to the AlertRulesTable.
    const paginationComp = useMemo((): AlertRulesTableProps['paginationComp'] => {
        return (
            <PaginationBar {...{
                itemsPerPageList: ITEMS_PER_PAGE_LIST,
                itemsPerPage    : searchParams.itemsPerPage,
                pagingMinPages  : PAGING_MIN_PAGES,
                currentPage     : searchParams.currentPage,
                totalItems      : alertRules && alertRules.range?.total,
                hasMoreItems    : (alertRules && alertRules.range
                    ? alertRules.range.total > searchParams.itemsPerPage * (searchParams.currentPage + 1)
                    : (!alertRules || alertRules.items.length >= searchParams.itemsPerPage)),
                texts: {
                    itemsPerPage    : capitalizeAll(trans('label.paging.items_per_page', { item_name: trans('label.item', { count: 2 }) })),
                    pageNTemplate   : capitalizeAll(trans('label.page_n')),
                    pageNofMTemplate: capitalizeAll(trans('label.page_n_of_m')),
                },
                onItemsPerPageChange,
                onNextPage,
                onPrevPage,
                onPage,
            }}
            />
        );
    }, [searchParams.itemsPerPage, searchParams.currentPage, alertRules, trans, onItemsPerPageChange, onNextPage, onPrevPage, onPage]);


    /**
     * Create and cache secondary Actions for the AlertRulesTable.
     */
    const alertRulesTableSecondaryActionsSettings = useMemo((): AlertRulesTableProps['customSecondaryActions'] => {
        if (canEditAlertRules) {
            return [{
                label  : capitalizeAll(trans('alerting.create_alert_rule.label.heartbeat')),
                icon   : 'add',
                variant: 'contained',
                color  : 'primary',
                onClick: () => {
                    onAlertRuleSelected(AlertConditionType.METRIC_HEARTBEAT);
                },
            }, {
                label  : capitalizeAll(trans('alerting.create_alert_rule.label.threshold')),
                icon   : 'add',
                variant: 'contained',
                color  : 'primary',
                onClick: () => {
                    onAlertRuleSelected(AlertConditionType.METRIC_THRESHOLD);
                },
            }];
        }
        return undefined;
    }, [canEditAlertRules, onAlertRuleSelected, trans]);


    return (
        <AlertRulesTable
            title={capitalizeAll(trans('alerting.label.alert_rule_other'))}
            icon="notifications"
            fieldsSettings={alertRulesTableFieldsSettings}
            customSecondaryActions={alertRulesTableSecondaryActionsSettings}
            listData={localizedAlertRulesList}
            onListItemSelected={onAlertRuleSelected}
            paginationComp={paginationComp}
            noDataMessageComp={(
                <GenericMessageBox
                    title={capitalizeAll(trans('generic.no_data.title', { term: trans('alerting.label.alert_rule', { count: 2 }) }))}
                />
            )}
            emptyDataMessageComp={(
                <GenericMessageBox
                    title={capitalizeAll(trans('generic.no_data.title', { term: trans('alerting.label.alert_rule', { count: 2 }) }))}
                />
            )}
        />
    );
};



// Register this component with the RitzComponentsCollection so that it can be used with the Ritz UI customization system
RitzComponentsCollection.getInstance().registerComponent('AlertRulesListingContainer', AlertRulesListingContainer);
